import React from 'react';
import { useViewEvent, UseViewEventData } from '@stitch-fix/event-reporter';
import OutfitImageItem from './OutfitImageItem';
import { ImageComponent } from './types';
import ShoppableOutfitBase from './ShoppableOutfitBase';

type OutfitViewEventData = Extract<UseViewEventData, { schema: 'outfit_view' }>;

export type ShoppableOutfitProps = {
  /**
   * The aspect ratio
   *
   * @default 1.0
   */
  aspectRatio?: number;

  /**
   * How many times the dots on the outfit items are reanimated. The dots
   * animate on first render, but we allow the animation to be replayed
   * to signal that each item is clickable.
   *
   * @default 0
   */
  dotRerenderCount?: number;

  /**
   * Optional partial event object to override or extend the built-in product tracking event data.
   */
  eventOverrides?: { view: Partial<OutfitViewEventData> };

  /**
   * An array of image data for each item in an outfit.
   */
  imageComponents: ImageComponent[];

  /**
   * Callback to execute when an item in the
   * outfit is clicked. Helpful for app-specific
   * analytics.
   *
   */
  onOutfitItemClick?: (arg0: ImageComponent) => void;

  /**
   * The outfit ID
   */
  outfitId: string;
};

/**
 * We use ShoppableOutfits to show specific styles and looks to clients.
 *
 * Exposed via a nested import: `import ShoppableOutfit from '@stitch-fix/mode-react/ShoppableOutfit';`
 *
 * This component is pre-instrumented with Product Tracking Events (PTEs).
 *
 * This prototype component was created by the LEO team.
 */
const ShoppableOutfit = ({
  outfitId,
  dotRerenderCount = 0,
  eventOverrides,
  aspectRatio = 1.0,
  imageComponents,
  onOutfitItemClick,
}: ShoppableOutfitProps) => {
  const { ref, EventProvider } = useViewEvent({
    schema: 'outfit_view',
    name: 'outfit_card',
    outfit_id: outfitId,
    ...eventOverrides?.view,
  });

  return (
    <EventProvider>
      <ShoppableOutfitBase aspectRatio={aspectRatio} ref={ref}>
        {imageComponents.map(imageComponent => (
          <OutfitImageItem
            dotRerenderCount={dotRerenderCount}
            imageComponent={imageComponent}
            onOutfitItemClick={onOutfitItemClick}
            key={imageComponent.id}
          />
        ))}
      </ShoppableOutfitBase>
    </EventProvider>
  );
};

ShoppableOutfit.displayName = 'ShoppableOutfit';

export default ShoppableOutfit;
